import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setUser } from '../../../userSlice';
import { clearAllChats } from '../../../chatSlice';
import { AppDispatch } from '../../../store';
import './styles/AuthPage.scss';
import { loginUser, getCurrentUser, setTokens, registerUser, sendRegistrationCode, getSpecialtyProfessions } from '../../utils/apiUtils';
import CustomSelect from '../../components/CustomSelect';
import AccountActivationPending from './components/AccountActivationPending';
import { useActivationCheck } from '../../hooks/useActivationCheck';
import { useSnackbar } from 'notistack';
import { useBrand } from '../../../brands/contexts/BrandContext';
import { useI18n } from '../../contexts/I18nContext';
import languageIcon from '../../../assets/language.svg';

interface FormData {
  email: string;
  password: string;
}

interface RegisterData {
  phone_number: string;
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  confirm_password: string;
  specialty: string;
  organization: string;
}

interface FormErrors {
  email?: string;
  password?: string;
}

interface RegisterErrors {
  phone_number?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  password?: string;
  confirm_password?: string;
  specialty?: string;
  organization?: string;
  verification_code?: string;
}

const AuthPage: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { brand } = useBrand();
  const { t, language, setLanguage } = useI18n();
  const [isLoginMode, setIsLoginMode] = useState(true);

  // Получаем путь к логотипу из конфигурации бренда
  const getAuthLogo = () => {
    return `${process.env.PUBLIC_URL}/${brand.logos.auth}`;
  };

  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: ''
  });

  const [registerData, setRegisterData] = useState<RegisterData>({
    phone_number: '',
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirm_password: '',
    specialty: '',
    organization: 'Public'
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [registerErrors, setRegisterErrors] = useState<RegisterErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  // Состояния для кода подтверждения
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [timer, setTimer] = useState(0); // Таймер в секундах (10 минут = 600)
  const [isSendingCode, setIsSendingCode] = useState(false);

  // Список профессий для выбора специальности (из /specialties/professions)
  const [specialtyProfessions, setSpecialtyProfessions] = useState<string[]>([]);
  const [loadingProfessions, setLoadingProfessions] = useState(false);

  // Используем хук для проверки статуса активации
  const { isActivationPending } = useActivationCheck();

  // Проверяем, включена ли регистрация при загрузке компонента
  useEffect(() => {
    if (process.env.REACT_APP_ENABLE_REGISTRATION !== 'true' && !isLoginMode) {
      setIsLoginMode(true);
    }
  }, [isLoginMode]);

  // Загружаем список профессий при показе формы регистрации
  useEffect(() => {
    if (!isLoginMode) {
      setLoadingProfessions(true);
      getSpecialtyProfessions()
        .then(setSpecialtyProfessions)
        .catch(() => setSpecialtyProfessions([]))
        .finally(() => setLoadingProfessions(false));
    }
  }, [isLoginMode]);

  // Таймер обратного отсчета для кода подтверждения
  useEffect(() => {
    if (codeSent && timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [codeSent, timer]);

  // Функция форматирования таймера
  const formatTimer = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Функция отправки кода подтверждения
  const handleSendCode = async () => {
    // Валидация email перед отправкой кода
    const emailError = validateEmail(registerData.email);
    if (emailError) {
      setRegisterErrors(prev => ({ ...prev, email: emailError }));
      enqueueSnackbar(t('auth.snackbar.emailInvalid'), {
        variant: 'error',
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        autoHideDuration: 4000
      });
      return;
    }

    setIsSendingCode(true);
    setApiError(null);

    try {
      await sendRegistrationCode(registerData.email);
      setCodeSent(true);
      setTimer(600); // 10 минут в секундах
      enqueueSnackbar('Код отправлен на email', {
        variant: 'success',
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        autoHideDuration: 4000
      });
    } catch (err: any) {
      const errorMessage = err.message || 'Ошибка отправки кода';
      enqueueSnackbar(errorMessage, {
        variant: 'error',
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        autoHideDuration: 4000
      });
      setApiError(errorMessage);
    } finally {
      setIsSendingCode(false);
    }
  };

  const validateEmail = (email: string): string | undefined => {
    if (!email) {
      return t('auth.validation.email.required');
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return t('auth.validation.email.invalid');
    }
    return undefined;
  };

  const validateLowPassword = (password: string): string | undefined => {
    if (!password) {
      return t('auth.validation.password.required');
    }
    return undefined;
  };

  const validatePassword = (password: string): string | undefined => {
    if (!password) {
      return t('auth.validation.password.required');
    }
    if (password.length < 8) {
      return t('auth.validation.password.minLength');
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return t('auth.validation.password.lowercase');
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return t('auth.validation.password.uppercase');
    }
    if (!/(?=.*\d)/.test(password)) {
      return t('auth.validation.password.digit');
    }
    return undefined;
  };

  const formatPhoneNumber = (value: string): string => {
    // Убираем все символы кроме цифр
    const numbers = value.replace(/\D/g, '');

    // Если номер пустой, возвращаем пустую строку
    if (numbers.length === 0) {
      return '';
    }

    // Если номер начинается с 8, заменяем на +7
    if (numbers.startsWith('8')) {
      const remainingNumbers = numbers.slice(1);
      // Ограничиваем до 10 цифр после +7
      return '+7' + remainingNumbers.slice(0, 10);
    }

    // Если номер начинается с 7, добавляем +
    if (numbers.startsWith('7')) {
      const remainingNumbers = numbers.slice(1);
      // Ограничиваем до 10 цифр после +7
      return '+7' + remainingNumbers.slice(0, 10);
    }

    // Если номер начинается с 9, добавляем +7
    if (numbers.startsWith('9')) {
      // Ограничиваем до 10 цифр после +7
      return '+7' + numbers.slice(0, 10);
    }

    // Если номер не подходит под форматы, возвращаем +79 + первые 8 цифр
    return '+79' + numbers.slice(0, 8);
  };

  const validatePhoneNumber = (phone: string): string | undefined => {
    // Телефон опционален, но если указан, должен быть валидным
    if (!phone) {
      return undefined; // Телефон не обязателен
    }
    const phoneRegex = /^\+7\d{10}$/;
    if (!phoneRegex.test(phone)) {
      return t('auth.validation.phone.invalid');
    }
    return undefined;
  };

  const validateRequiredField = (value: string, fieldName: string): string | undefined => {
    if (!value.trim()) {
      return t('auth.validation.field.required');
    }
    return undefined;
  };

  const validateConfirmPassword = (password: string, confirmPassword: string): string | undefined => {
    if (!confirmPassword) {
      return t('auth.validation.passwordConfirm.required');
    }
    if (password !== confirmPassword) {
      return t('auth.validation.passwordConfirm.mismatch');
    }
    return undefined;
  };

  const validateVerificationCode = (code: string): string | undefined => {
    if (!code) {
      return t('auth.validation.code.required');
    }
    if (!/^\d+$/.test(code)) {
      return t('auth.validation.code.digitsOnly');
    }
    if (code.length !== 6) {
      return t('auth.validation.code.length');
    }
    return undefined;
  };

  const validateForm = (): boolean => {
    const emailError = validateEmail(formData.email);
    const passwordError = validateLowPassword(formData.password);

    const newErrors: FormErrors = {};
    if (emailError) newErrors.email = emailError;
    if (passwordError) newErrors.password = passwordError;

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const validateRegisterForm = (): boolean => {
    const newErrors: RegisterErrors = {};

    newErrors.phone_number = validatePhoneNumber(registerData.phone_number);
    newErrors.first_name = validateRequiredField(registerData.first_name, 'Имя');
    newErrors.last_name = validateRequiredField(registerData.last_name, 'Фамилия');
    newErrors.email = validateEmail(registerData.email);
    newErrors.password = validatePassword(registerData.password);
    newErrors.confirm_password = validateConfirmPassword(registerData.password, registerData.confirm_password);
    newErrors.specialty = validateRequiredField(registerData.specialty, 'Специальность');
    newErrors.verification_code = validateVerificationCode(verificationCode);

    // Убираем undefined значения
    Object.keys(newErrors).forEach(key => {
      if (newErrors[key as keyof RegisterErrors] === undefined) {
        delete newErrors[key as keyof RegisterErrors];
      }
    });

    setRegisterErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleRegisterInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    let processedValue = value;

    // Применяем маску для номера телефона
    if (name === 'phone_number') {
      processedValue = formatPhoneNumber(value);
    }

    setRegisterData(prev => ({
      ...prev,
      [name]: processedValue
    }));

    // Clear error when user starts typing
    if (registerErrors[name as keyof RegisterErrors]) {
      setRegisterErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  // Обработчик изменения кода подтверждения
  const handleVerificationCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6); // Только цифры, максимум 6
    setVerificationCode(value);

    // Очищаем ошибку при вводе
    if (registerErrors.verification_code) {
      setRegisterErrors(prev => ({
        ...prev,
        verification_code: undefined
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError(null);
    if (!validateForm()) {
      return;
    }
    setIsSubmitting(true);
    try {
      // Очищаем кеш чатов перед входом в новый аккаунт
      dispatch(clearAllChats());

      // Выполняем вход с JWT токенами
      const loginResponse = await loginUser({
        email: formData.email,
        password: formData.password
      });

      // Сохраняем токены
      setTokens(loginResponse.access_token, loginResponse.refresh_token);

      // Получаем профиль пользователя
      const profile = await getCurrentUser();
      if (!profile) {
        throw new Error('Не удалось получить профиль пользователя');
      }

      // Устанавливаем пользователя в Redux
      dispatch(setUser({
        email: profile.email,
        phone_number: profile.phone_number || '',
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        id: profile.id || 0,
        role_name: profile.role_name || '',
        role_id: profile.role_id || 0,
        specialty: profile.specialty || '',
        is_active: profile.is_active,
        organization: profile.organization || 'Public',
        ...profile
      }));

      // Переходим на /dialogs через изменение URL без полной перезагрузки страницы
      // Redirector должен подхватить изменение auth состояния
      window.location.href = '/dialogs';
    } catch (err: any) {
      const errorMessage = err.message || t('auth.snackbar.loginError');
      enqueueSnackbar(errorMessage, {
        variant: 'error',
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        autoHideDuration: 4000
      });
      setApiError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError(null);

    // Проверяем, что код был отправлен
    if (!codeSent) {
      enqueueSnackbar(t('auth.snackbar.codeNotRequested'), {
        variant: 'error',
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        autoHideDuration: 4000
      });
      return;
    }

    if (!validateRegisterForm()) {
      return;
    }
    setIsSubmitting(true);
    try {
      const registerDataToSend: any = {
        email: registerData.email,
        first_name: registerData.first_name,
        last_name: registerData.last_name,
        specialty: registerData.specialty,
        organization: registerData.organization || 'LazyDoc',
        password: registerData.password,
        confirm_password: registerData.confirm_password,
        verification_code: verificationCode
      };

      // Добавляем phone_number только если он заполнен
      if (registerData.phone_number && registerData.phone_number.trim()) {
        registerDataToSend.phone_number = registerData.phone_number;
      }

      // Используем новую функцию регистрации
      await registerUser(registerDataToSend);

      // После успешной регистрации показываем диалог успеха
      setShowSuccessDialog(true);
      setApiError(null);

      // Показываем уведомление об успешной регистрации
      enqueueSnackbar(t('auth.snackbar.registerSuccess'), {
        variant: 'success',
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        autoHideDuration: 4000
      });

      // Очищаем форму регистрации
      setRegisterData({
        phone_number: '',
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        confirm_password: '',
        specialty: '',
        organization: 'LazyDoc',
      });
      setVerificationCode('');
      setCodeSent(false);
      setTimer(0);

    } catch (err: any) {
      const errorMessage = err.message || t('auth.snackbar.registerError');
      enqueueSnackbar(errorMessage, {
        variant: 'error',
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        autoHideDuration: 4000
      });
      setApiError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const switchToRegister = () => {
    // Проверяем, включена ли регистрация
    if (process.env.REACT_APP_ENABLE_REGISTRATION !== 'true') {
      return;
    }
    setIsLoginMode(false);
    setApiError(null);
    setErrors({});
    setRegisterErrors({});
    setVerificationCode('');
    setCodeSent(false);
    setTimer(0);
  };

  const switchToLogin = () => {
    setIsLoginMode(true);
    setApiError(null);
    setErrors({});
    setRegisterErrors({});
    setVerificationCode('');
    setCodeSent(false);
    setTimer(0);
  };

  const closeSuccessDialog = () => {
    setShowSuccessDialog(false);
    setIsLoginMode(true);

    // Очищаем форму логина для удобства пользователя
    setFormData({
      email: '',
      password: ''
    });

    // Показываем уведомление о переходе к форме входа
    enqueueSnackbar(t('auth.snackbar.nowYouCanLogin'), {
      variant: 'info',
      anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
      autoHideDuration: 3000
    });
  };

  // Если аккаунт не активирован, показываем страницу ожидания
  if (isActivationPending) {
    return <AccountActivationPending />;
  }

  return (
    <div id='auth-page'>
      <div className='auth-page-container container d-flex flex-column align-items-center justify-content-center'>
        <img src={getAuthLogo()} alt="logo" />
        <div className='auth-page-form d-flex flex-column align-items-center justify-content-center'>
          <h2 className='auth-page-form-title'>
            {isLoginMode ? t('auth.title.login') : t('auth.title.register')}
          </h2>

          {!isLoginMode && (
            <div className='form-required-notice'>
              <span>{t('auth.form.requiredNotice')}</span>
            </div>
          )}
          {isLoginMode ? (
            // Форма входа
            <form className='d-flex flex-column gap-2 w-100 mt-3' onSubmit={handleSubmit}>
              {/*{apiError && <div className='error-message'>{apiError}</div>}*/}
              <div className='input-group'>
                <input
                  type="email"
                  name="email"
                  placeholder='Email'
                  className={`input-primary unrounded ${errors.email ? 'input-error' : ''}`}
                  value={formData.email}
                  onChange={handleInputChange}
                />
                {errors.email && <div className='error-message'>{errors.email}</div>}
              </div>

              <div className='input-group'>
                <div className='password-input-wrapper'>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder={t('auth.field.password.required')}
                    className={`input-primary unrounded ${errors.password ? 'input-error' : ''}`}
                    value={formData.password}
                    onChange={handleInputChange}
                  />
                  <button
                    type="button"
                    className='password-toggle-btn'
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
                {errors.password && <div className='error-message'>{errors.password}</div>}
              </div>

              <button
                type="submit"
                className={`button-primary mt-3 ${isSubmitting ? 'disabled' : ''}`}
                disabled={isSubmitting}
              >
                {isSubmitting ? t('auth.login.submit.loading') : t('auth.login.submit.idle')}
              </button>

              {process.env.REACT_APP_ENABLE_REGISTRATION === 'true' && (
                <div className='auth-switch-mode'>
                  <span>{t('auth.noAccount')}</span>
                  <button
                    type="button"
                    className='link-button'
                    onClick={switchToRegister}
                  >
                    {t('auth.toRegister')}
                  </button>
                </div>
              )}
            </form>
          ) : (
            // Форма регистрации
            <form className='d-flex flex-column gap-2 w-100' onSubmit={handleRegisterSubmit}>
              {apiError && <div className='error-message'>{apiError}</div>}

              <h3 className='form-section-title'>{t('auth.section.personal')}</h3>
              <div className='input-group'>
                <input
                  type="text"
                  name="first_name"
                  placeholder={t('auth.field.firstName')}
                  className={`input-primary unrounded ${registerErrors.first_name ? 'input-error' : ''}`}
                  value={registerData.first_name}
                  onChange={handleRegisterInputChange}
                />
                {registerErrors.first_name && <div className='error-message'>{registerErrors.first_name}</div>}
              </div>

              <div className='input-group'>
                <input
                  type="text"
                  name="last_name"
                  placeholder={t('auth.field.lastName')}
                  className={`input-primary unrounded ${registerErrors.last_name ? 'input-error' : ''}`}
                  value={registerData.last_name}
                  onChange={handleRegisterInputChange}
                />
                {registerErrors.last_name && <div className='error-message'>{registerErrors.last_name}</div>}
              </div>

              <div className='input-group'>
                <CustomSelect
                  options={specialtyProfessions.map((p) => ({ value: p, label: p }))}
                  value={registerData.specialty}
                  onChange={(val) => {
                    setRegisterData((prev) => ({ ...prev, specialty: String(val) }));
                    if (registerErrors.specialty) {
                      setRegisterErrors((prev) => ({ ...prev, specialty: undefined }));
                    }
                  }}
                  placeholder={loadingProfessions ? t('auth.loadingSpecialties') : t('auth.specialtyPlaceholder')}
                  className={registerErrors.specialty ? 'input-error' : ''}
                  disabled={loadingProfessions}
                  searchable
                  searchPlaceholder={t('auth.searchSpecialty')}
                />
                {registerErrors.specialty && <div className='error-message'>{registerErrors.specialty}</div>}
              </div>

              <h3 className='form-section-title'>{t('auth.section.contact')}</h3>
              <div className='input-group'>
                <input
                  type="tel"
                  name="phone_number"
                  placeholder={t('auth.field.phoneOptional')}
                  className={`input-primary unrounded ${registerErrors.phone_number ? 'input-error' : ''}`}
                  value={registerData.phone_number}
                  onChange={handleRegisterInputChange}
                />
                {registerErrors.phone_number && <div className='error-message'>{registerErrors.phone_number}</div>}
              </div>

              <div className='input-group'>
                <input
                  type="email"
                  name="email"
                  placeholder={t('auth.field.email.required')}
                  className={`input-primary unrounded ${registerErrors.email ? 'input-error' : ''}`}
                  value={registerData.email}
                  onChange={handleRegisterInputChange}
                />
                {registerErrors.email && <div className='error-message'>{registerErrors.email}</div>}
              </div>

              {/* Кнопка отправки кода подтверждения */}
              <div className='input-group send-code-group'>
                <button
                  type="button"
                  className={`button-primary ${isSendingCode || (codeSent && timer > 0) ? 'disabled' : ''}`}
                  onClick={handleSendCode}
                  disabled={isSendingCode || (codeSent && timer > 0) || !registerData.email || !!validateEmail(registerData.email)}
                >
                  {isSendingCode
                    ? 'Отправка...'
                    : codeSent && timer > 0
                      ? `Отправить код повторно (${formatTimer(timer)})`
                      : 'Отправить код подтверждения'}
                </button>
              </div>

              {/* Поле для ввода кода подтверждения (показывать только после отправки кода) */}
              {codeSent && (
                <div className='input-group'>
                  <input
                    type="text"
                    name="verification_code"
                    placeholder='Введите 6-значный код *'
                    className={`input-primary unrounded ${registerErrors.verification_code ? 'input-error' : ''}`}
                    value={verificationCode}
                    onChange={handleVerificationCodeChange}
                    maxLength={6}
                    pattern="[0-9]{6}"
                    inputMode="numeric"
                  />
                  {registerErrors.verification_code && <div className='error-message'>{registerErrors.verification_code}</div>}
                </div>
              )}

              <h3 className='form-section-title'>{t('auth.section.security')}</h3>
              <div className='input-group'>
                <div className='password-input-wrapper'>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder={t('auth.field.password.required')}
                    className={`input-primary unrounded ${registerErrors.password ? 'input-error' : ''}`}
                    value={registerData.password}
                    onChange={handleRegisterInputChange}
                  />
                  <button
                    type="button"
                    className='password-toggle-btn'
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
                {registerErrors.password && <div className='error-message'>{registerErrors.password}</div>}
                <div className='password-requirements'>
                  <small>{t('auth.password.requirements')}</small>
                </div>
              </div>

              <div className='input-group'>
                <div className='password-input-wrapper'>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirm_password"
                    placeholder={t('auth.field.passwordConfirm.required')}
                    className={`input-primary unrounded ${registerErrors.confirm_password ? 'input-error' : ''}`}
                    value={registerData.confirm_password}
                    onChange={handleRegisterInputChange}
                  />
                  <button
                    type="button"
                    className='password-toggle-btn'
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
                {registerErrors.confirm_password && <div className='error-message'>{registerErrors.confirm_password}</div>}
              </div>

              <button
                type="submit"
                className={`button-primary mt-3 ${isSubmitting ? 'disabled' : ''}`}
                disabled={isSubmitting}
              >
                {isSubmitting ? t('auth.register.submit.loading') : t('auth.register.submit.idle')}
              </button>

              <div className='auth-switch-mode'>
                <span>{t('auth.haveAccount')}</span>
                <button
                  type="button"
                  className='link-button'
                  onClick={switchToLogin}
                >
                  {t('auth.toLogin')}
                </button>
              </div>
            </form>
          )}
        </div>


        <div className='auth-language-switch mt-2 d-flex flex-row'>
          <img src={languageIcon} alt="logo" className='auth-language-icon p-2' />

          <button
            type="button"
            className={`auth-language-btn ${language === 'ru' ? 'active' : ''}`}
            onClick={() => setLanguage('ru')}
          >
            {t('profile.language.ru')}
          </button>
          <button
            type="button"
            className={`auth-language-btn ${language === 'en' ? 'active' : ''}`}
            onClick={() => setLanguage('en')}
          >
            {t('profile.language.en')}
          </button>

        </div>
      </div>

      {/* Диалог успешной регистрации */}
      {showSuccessDialog && (
        <div className="success-dialog-overlay">
          <div className="success-dialog">
            <div className="success-dialog-content">
              <div className="success-icon">✅</div>
              <h3>{t('auth.success.title')}</h3>
              <p>{t('auth.success.line1')}</p>
              <p>{t('auth.success.line2')}</p>
              <button
                type="button"
                className="button-primary"
                onClick={closeSuccessDialog}
              >
                {t('auth.success.button')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthPage;
